import { useCallback, useEffect, useRef, useState } from 'react'
import { NextRouter } from 'next/router'

import { EventDetectionMethod, EventType, TestPrepGrade } from '~/behavior-tracking/types'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'
import ROUTES from '~/shared/constants/routes.const'

const PULSE_INTERVAL = 30_000

/**
 * Time spent is the sum of ≤ 45 s gaps between pulses (hom-api and the warehouse), so a pulse is a claim that the
 * learner is here. A visible tab nobody touches would otherwise pulse forever: after this long without input, it
 * stops and asks whether they're still there (`StillThereModal`), and stays quiet until they answer.
 */
export const IDLE_AFTER = 30 * 60_000

// `scroll` doesn't bubble, so every listener is on window in the capture phase.
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'] as const

/** A playing video is learning without input. */
function isVideoPlaying(): boolean {
  return Array.from(document.querySelectorAll('video')).some((video) => !video.paused && !video.ended)
}

/**
 * Input inside a cross-origin iframe (the games, Homebase) never reaches this document. Focus moving into one, and
 * every message one posts while it's played (rewards, analytics, saved progress), stand in for that input. Focus
 * merely resting in an iframe doesn't: it stays there after one click, so an idle game would pulse forever.
 */
function isFromIframe(source: MessageEventSource | null): boolean {
  return (
    source != null && Array.from(document.querySelectorAll('iframe')).some((frame) => frame.contentWindow === source)
  )
}

/**
 * ⚠ The games are exempt from the cutoff until they send a heartbeat. Input inside a game never reaches this page,
 * and the mult game's and Wind Surfer's survival runs post nothing until they end, so a good run can outlast the
 * cutoff and get interrupted. Once each game posts a message on input (throttled to every 30–60 s), drop this list:
 * any message from an iframe on the page already counts as presence. ClickUp: https://app.clickup.com/t/869fngmxf
 */
const GAME_PAGES: readonly string[] = [
  ROUTES.MULT_GAME,
  '/games/heming',
  ROUTES.WINDSURFER,
  ROUTES.STACKNLOAD,
  ROUTES.TREASURETRAIL,
]

export interface Presence {
  /** Idle past the cutoff: no pulses until `confirmPresence`, whatever input comes in meanwhile. */
  isIdle: boolean
  confirmPresence: () => void
}

export default function useUserPulseTracker(
  router: NextRouter,
  userId: number | null,
  testPrepGrade?: TestPrepGrade,
): Presence {
  const lastInputAt = useRef(Date.now())
  // The interval reads the ref; the state is for rendering the prompt.
  const isIdleRef = useRef(false)
  const [isIdle, setIsIdle] = useState(false)

  const confirmPresence = useCallback((): void => {
    lastInputAt.current = Date.now()
    isIdleRef.current = false
    setIsIdle(false)
  }, [])

  // Logging in or out, or switching profile, is the learner acting: whoever is tracked now starts fresh.
  useEffect(() => {
    confirmPresence()
  }, [userId, confirmPresence])

  useEffect(() => {
    const markActive = (): void => {
      lastInputAt.current = Date.now()
    }
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        markActive()
      }
    }
    // The window blurs as focus moves into an iframe on this page.
    const onBlur = (): void => {
      if (document.activeElement instanceof HTMLIFrameElement) {
        markActive()
      }
    }
    const onMessage = (event: MessageEvent): void => {
      if (isFromIframe(event.source)) {
        markActive()
      }
    }

    INPUT_EVENTS.forEach((type) => window.addEventListener(type, markActive, { capture: true, passive: true }))
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('blur', onBlur)
    window.addEventListener('message', onMessage)

    return () => {
      INPUT_EVENTS.forEach((type) => window.removeEventListener(type, markActive, { capture: true }))
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('message', onMessage)
    }
  }, [])

  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (userId == null) {
        return
      }

      if (document.hidden || isIdleRef.current) {
        return
      }

      // `pathname` leaves out the query, so `/games/multgame?championship=hom` matches too.
      if (isVideoPlaying() || GAME_PAGES.includes(router.pathname)) {
        lastInputAt.current = Date.now()
      } else if (Date.now() - lastInputAt.current > IDLE_AFTER) {
        isIdleRef.current = true
        setIsIdle(true)
        return
      }

      const event = {
        eventName: EventType.PULSE,
        viewKey: router.asPath,
        extra: {
          detectBy: EventDetectionMethod.PULSE_INTERVAL_EVENT,
          testPrepGrade,
        },
      }

      await logUserActivity(userId, event)
    }, PULSE_INTERVAL)

    return () => {
      clearInterval(intervalId)
    }
  }, [router.asPath, router.pathname, testPrepGrade, userId])

  return { isIdle, confirmPresence }
}
